import {
  isStaleAnswersRevisionError,
  type Document,
  type DocumentAnswerChange,
  type DocumentAnswers,
  type NoteAnswersUpdate,
  type TranscriptorApiClient,
} from '@/shared/api/transcriptor';

// A protocol document whose answers are keyed by the template version it pinned.
export const isLiveProtocolDocument = (document: Document) =>
  document.type === 'protocol' && !!document.id && !!document.template_id;

// A manual edit that the server has not accepted yet. A block note keeps
// its operation, so the note can show a value before the server's view.
export type PendingAnswer =
  | { value: string }
  | { release: true }
  | { note: NoteAnswersUpdate };

// Responses can arrive out of order; only a newer snapshot replaces the
// applied one.
export const isNewerDocumentAnswers = (
  current: DocumentAnswers | null | undefined,
  next: DocumentAnswers,
) =>
  !current ||
  next.revision > current.revision ||
  (next.revision === current.revision &&
    next.transcriptRevision > current.transcriptRevision);

// A legacy answer is changed by its key; a block note sends its operations
// as scribe takes them.
type AnswerEditPayload =
  | { change: DocumentAnswerChange; note?: undefined }
  | { note: NoteAnswersUpdate; change?: undefined };

export type AnswerEdit = AnswerEditPayload & {
  key: string;
  pending: PendingAnswer;
  // Choices are sent at once; typed text waits for a short pause.
  immediate: boolean;
  // The answers the server stored with this edit, e.g. for its undo.
  onStored?: (answers: DocumentAnswers) => void;
};

export type EditedDocument = {
  // The last snapshot the server accepted.
  snapshot: DocumentAnswers | null;
  pending: Record<string, PendingAnswer>;
  saveFailed: boolean;
};

export type AnswerEditsApi = Pick<
  TranscriptorApiClient,
  'getDocumentAnswers' | 'updateDocumentAnswers'
>;

type QueuedEdit = Pick<AnswerEdit, 'change' | 'note' | 'onStored'> & {
  version: number;
};

const TEXT_EDIT_DELAY_MS = 600;

// Sends the clinician's answer edits. Each document has at most one request
// in flight; edits made meanwhile go out together after it. A stale revision
// is read again and the same edits are sent once more on top of it.
export const createAnswerEdits = ({
  encounterId,
  api,
  readRevision,
  updateDocument,
  onSaved,
}: {
  encounterId: string;
  api: AnswerEditsApi;
  readRevision: (documentId: string) => number;
  updateDocument: (
    documentId: string,
    recipe: (document: EditedDocument) => EditedDocument,
  ) => void;
  onSaved?: (documentId: string) => void;
}) => {
  const runtime = {
    disposed: false,
    edits: {} as Record<string, Record<string, QueuedEdit>>,
    versions: {} as Record<string, Record<string, number>>,
    saving: {} as Record<string, boolean>,
    timers: {} as Record<string, number>,
  };

  const applySnapshot = (
    documentId: string,
    answers: DocumentAnswers | null,
  ) => {
    if (answers) {
      updateDocument(documentId, (document) =>
        isNewerDocumentAnswers(document.snapshot, answers)
          ? { ...document, snapshot: answers }
          : document,
      );
    }
  };

  const settle = (
    documentId: string,
    sent: [string, QueuedEdit][],
    failed: boolean,
  ) =>
    updateDocument(documentId, (document) => ({
      ...document,
      saveFailed: failed || document.saveFailed,
      // An edit made again while this one was in flight stays pending.
      pending: Object.fromEntries(
        Object.entries(document.pending).filter(
          ([key]) =>
            !sent.some(
              ([sentKey, item]) =>
                sentKey === key &&
                runtime.versions[documentId]?.[key] === item.version,
            ),
        ),
      ),
    }));

  const flush = async (documentId: string): Promise<void> => {
    const queued = Object.entries(runtime.edits[documentId] ?? {});
    if (runtime.disposed || runtime.saving[documentId] || !queued.length) {
      return;
    }
    runtime.edits = { ...runtime.edits, [documentId]: {} };
    runtime.saving = { ...runtime.saving, [documentId]: true };
    const noteUpdates = queued.flatMap(([, item]) =>
      item.note ? [item.note] : [],
    );
    const submit = () =>
      api.updateDocumentAnswers({
        encounterId,
        documentId,
        baseRevision: readRevision(documentId),
        changes: queued.flatMap(([, item]) =>
          item.change ? [item.change] : [],
        ),
        ...(noteUpdates.length && { noteUpdates }),
      });
    try {
      const answers = await submit().catch(async (error: unknown) => {
        if (!isStaleAnswersRevisionError(error)) {
          throw error;
        }
        applySnapshot(
          documentId,
          await api.getDocumentAnswers({ encounterId, documentId }),
        );
        return submit();
      });
      applySnapshot(documentId, answers);
      if (answers) {
        queued.forEach(([, item]) => item.onStored?.(answers));
      }
      settle(documentId, queued, false);
      if (!runtime.disposed) {
        onSaved?.(documentId);
      }
    } catch {
      settle(documentId, queued, true);
    } finally {
      runtime.saving = { ...runtime.saving, [documentId]: false };
    }
    return flush(documentId);
  };

  return {
    applySnapshot,

    edit: (
      documentId: string,
      { key, pending, immediate, change, note, onStored }: AnswerEdit,
    ) => {
      const version = (runtime.versions[documentId]?.[key] ?? 0) + 1;
      runtime.versions = {
        ...runtime.versions,
        [documentId]: { ...runtime.versions[documentId], [key]: version },
      };
      runtime.edits = {
        ...runtime.edits,
        [documentId]: {
          ...runtime.edits[documentId],
          [key]: { change, note, onStored, version },
        },
      };
      updateDocument(documentId, (document) => ({
        ...document,
        saveFailed: false,
        pending: { ...document.pending, [key]: pending },
      }));
      window.clearTimeout(runtime.timers[documentId]);
      if (immediate) {
        void flush(documentId);
        return;
      }
      runtime.timers = {
        ...runtime.timers,
        [documentId]: window.setTimeout(
          () => void flush(documentId),
          TEXT_EDIT_DELAY_MS,
        ),
      };
    },

    dispose: () => {
      runtime.disposed = true;
      Object.values(runtime.timers).forEach((timer) =>
        window.clearTimeout(timer),
      );
    },
  };
};

export type StoredAnswersState = EditedDocument & {
  // False until the first read finished, whether it succeeded or not.
  loaded: boolean;
};

// The answers of one finished protocol document, editable by the clinician.
// A failed read leaves no snapshot, so the caller shows the document text.
export const createStoredAnswers = ({
  encounterId,
  documentId,
  api,
  onChange,
  onSaved,
}: {
  encounterId: string;
  documentId: string;
  api: AnswerEditsApi;
  onChange: (state: StoredAnswersState) => void;
  onSaved?: () => void;
}) => {
  const runtime = {
    disposed: false,
    state: {
      snapshot: null,
      pending: {},
      saveFailed: false,
      loaded: false,
    } as StoredAnswersState,
  };

  const emit = (recipe: (state: StoredAnswersState) => StoredAnswersState) => {
    if (!runtime.disposed) {
      runtime.state = recipe(runtime.state);
      onChange(runtime.state);
    }
  };

  const edits = createAnswerEdits({
    encounterId,
    api,
    readRevision: () => runtime.state.snapshot?.revision ?? 0,
    updateDocument: (_, recipe) =>
      emit((state) => ({ ...state, ...recipe(state) })),
    onSaved: () => onSaved?.(),
  });

  return {
    load: async () => {
      const answers = await api
        .getDocumentAnswers({ encounterId, documentId })
        .catch(() => null);
      edits.applySnapshot(documentId, answers);
      emit((state) => (state.loaded ? state : { ...state, loaded: true }));
    },
    // Answers another request stored; older revisions are ignored.
    receive: (answers: DocumentAnswers) =>
      edits.applySnapshot(documentId, answers),
    edit: (edit: AnswerEdit) => edits.edit(documentId, edit),
    dispose: () => {
      runtime.disposed = true;
      edits.dispose();
    },
  };
};

export type StoredAnswers = ReturnType<typeof createStoredAnswers>;
